import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ApiService, { NotFoundError } from './services/ApiService';
import UrlService from './services/UrlService';
import DecryptionError from './services/DecryptionError';
import NotFoundScreen from './components/NotFoundScreen';
import LinkText from './components/LinkText';
import LanguageSwitcher from './components/LanguageSwitcher';
import { usePageMetadata } from './hooks/usePageMetadata';

function RankedElectionResult() {
    const { t, i18n } = useTranslation(['results', 'common', 'election']);
    const [electionResult, setElectionResult] = useState(null);
    const [electionInfo, setElectionInfo] = useState({});
    const [allVotes, setAllVotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDecryptionError, setIsDecryptionError] = useState(false);
    const [isNotFoundError, setIsNotFoundError] = useState(false);
    const [copySuccess, setCopySuccess] = useState('');
    
    // Veto controls
    const [vetoThreshold, setVetoThreshold] = useState(1);
    const [areVetoesConsidered, setAreVetoesConsidered] = useState(true);
    const { id } = useParams();
    const location = useLocation();

    // Page metadata
    const { title, description } = usePageMetadata(electionInfo);

    useEffect(() => {
        // Extract admin keys from URL
        UrlService.extractAndStoreAdminKeys(location, id);
        // Extract encryption keys from URL
        UrlService.extractAndStoreEncryptionKeys(location, id);

        setLoading(true);

        // Fetch election info, results, and all votes for veto calculation
        Promise.all([
            ApiService.getElection(id),
            ApiService.getElectionResult(id),
            ApiService.getVotes(id)
        ])
        .then(([electionData, resultData, votesData]) => {
            setElectionInfo(electionData);
            setElectionResult(resultData);
            setAllVotes(votesData);
            setLoading(false);
            setError(null);
            setIsDecryptionError(false);

            // Sync URL with correct encryption params from localStorage
            UrlService.syncEncryptionKeysWithUrl(id);
        })
        .catch(error => {
            console.error('Error fetching election result:', error);
            if (error instanceof NotFoundError) {
                setIsNotFoundError(true);
            } else if (error instanceof DecryptionError) {
                setError(t('common:errors.decryptionFailed'));
                setIsDecryptionError(true);
            } else {
                setError(t('results:errors.loadFailed'));
            }
            setLoading(false);
        });
    }, [id, location]); // eslint-disable-line react-hooks/exhaustive-deps

    // Calculate veto counts for each option
    const vetoCountsByOption = React.useMemo(() => {
        const counts = {};
        allVotes.forEach(vote => {
            vote.options?.forEach(voteOption => {
                if (voteOption.isVetoed) {
                    const optionId = voteOption.rankedElectionOptionId || voteOption.id;
                    counts[optionId] = (counts[optionId] || 0) + 1;
                }
            });
        });
        return counts;
    }, [allVotes]);

    // Determine which options are vetoed based on threshold and toggle
    const getVetoedOptionIds = React.useMemo(() => {
        if (!areVetoesConsidered) {
            return new Set();
        }
        const vetoedIds = new Set();
        Object.entries(vetoCountsByOption).forEach(([optionId, count]) => {
            if (count >= vetoThreshold) {
                vetoedIds.add(optionId);
            }
        });
        return vetoedIds;
    }, [vetoCountsByOption, vetoThreshold, areVetoesConsidered]);

    // Separate options into non-vetoed and vetoed groups
    const { nonVetoedOptions, vetoedOptions } = React.useMemo(() => {
        const nonVetoed = {};
        const vetoed = {};
        
        electionResult?.options?.forEach(option => {
            const isVetoed = getVetoedOptionIds.has(option.id);
            const target = isVetoed ? vetoed : nonVetoed;
            
            if (!target[option.rank]) {
                target[option.rank] = [];
            }
            target[option.rank].push({
                ...option,
                vetoCount: vetoCountsByOption[option.id] || 0
            });
        });
        
        return { nonVetoedOptions: nonVetoed, vetoedOptions: vetoed };
    }, [electionResult, getVetoedOptionIds, vetoCountsByOption]);

    const sortedNonVetoedRanks = Object.keys(nonVetoedOptions).sort((a, b) => parseInt(a) - parseInt(b));
    const sortedVetoedRanks = Object.keys(vetoedOptions).sort((a, b) => parseInt(a) - parseInt(b));

    // For backward compatibility and original functionality
    const optionsByRank = areVetoesConsidered ? nonVetoedOptions : electionResult?.options?.reduce((acc, option) => {
        if (!acc[option.rank]) {
            acc[option.rank] = [];
        }
        acc[option.rank].push({
            ...option,
            vetoCount: vetoCountsByOption[option.id] || 0
        });
        return acc;
    }, {}) || {};

    const sortedRanks = Object.keys(optionsByRank).sort((a, b) => parseInt(a) - parseInt(b));

    // Helper function to render an option with veto count
    const renderOptionWithVetoCount = (option, showTied = false, showVetoCounts = true) => (
        <div key={option.id} className="option-name">
            <LinkText text={option.name} showImagePreviews={true} />
            {showVetoCounts && option.vetoCount > 0 && (
                <span className="veto-count-badge ms-2">
                    {t('results:veto.vetoCount', { count: option.vetoCount })}
                </span>
            )}
            {showTied && <span className="tie-indicator">{t('results:winner.tied')}</span>}
        </div>
    );

    const generateRankingText = (optionsByRank, sortedRanks, t, areVetoesConsidered, vetoThreshold, nonVetoedOptions, sortedNonVetoedRanks, vetoedOptions, sortedVetoedRanks) => {
        if (areVetoesConsidered) {
            // Generate two-section format when vetoes are considered
            const lines = [];
            
            // Accepted Options section
            lines.push(t('results:sections.nonVetoed') + ':');
            lines.push(''); // Empty line
            
            if (sortedNonVetoedRanks.length === 0) {
                lines.push(t('results:sections.noNonVetoedOptions'));
            } else {
                sortedNonVetoedRanks.forEach(rank => {
                    const options = nonVetoedOptions[rank];
                    if (options && options.length > 0) {
                        options.forEach((option, index) => {
                            const isTied = options.length > 1;
                            const tiedText = isTied ? ` ${t('results:tied')}` : '';
                            lines.push(`${rank}. ${option.name}${tiedText}`);
                        });
                    }
                });
            }
            
            // Add rejected section if there are vetoed options
            if (sortedVetoedRanks.length > 0) {
                lines.push(''); // Empty line
                lines.push(t('results:sections.rejectedWithThreshold', { threshold: vetoThreshold, count: vetoThreshold }) + ':');
                
                sortedVetoedRanks.forEach(rank => {
                    const options = vetoedOptions[rank];
                    if (options && options.length > 0) {
                        options.forEach((option, index) => {
                            const vetoCountText = option.vetoCount > 0 ? ` (vetoes: ${option.vetoCount})` : '';
                            lines.push(`- ${option.name}${vetoCountText}`);
                        });
                    }
                });
            }
            
            return lines.join('\n');
        } else {
            // Original simple format when vetoes are not considered
            if (!optionsByRank || !sortedRanks || sortedRanks.length === 0) {
                return '';
            }
            
            const lines = [];
            
            sortedRanks.forEach(rank => {
                const options = optionsByRank[rank];
                if (options && options.length > 0) {
                    options.forEach((option, index) => {
                        const isTied = options.length > 1;
                        const tiedText = isTied ? ` ${t('results:tied')}` : '';
                        lines.push(`${rank}. ${option.name}${tiedText}`);
                    });
                }
            });
            
            return lines.join('\n');
        }
    };

    const handleCopyRankingText = async () => {
        try {
            const rankingText = generateRankingText(
                optionsByRank, 
                sortedRanks, 
                t, 
                areVetoesConsidered, 
                vetoThreshold, 
                nonVetoedOptions, 
                sortedNonVetoedRanks, 
                vetoedOptions, 
                sortedVetoedRanks
            );
            if (!rankingText) {
                setCopySuccess(t('results:textCopyFailed'));
                setTimeout(() => setCopySuccess(''), 3000);
                return;
            }
            
            await navigator.clipboard.writeText(rankingText);
            setCopySuccess(t('results:rankingTextCopied'));
            setTimeout(() => setCopySuccess(''), 3000);
        } catch (err) {
            console.error('Failed to copy ranking text:', err);
            setCopySuccess(t('results:textCopyFailed'));
            setTimeout(() => setCopySuccess(''), 3000);
        }
    };

    if (loading) {
        return (
            <div className="result-container">
                <div className="container-fluid h-100">
                    <div className="row h-100 justify-content-center align-items-center">
                        <div className="col-12 text-center">
                            <div className="spinner-border text-light" role="status">
                                <span className="visually-hidden">{t('results:loading.text')}</span>
                            </div>
                            <p className="text-light mt-3">{t('results:loading.results')}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show not found error screen if election was not found
    if (isNotFoundError) {
        return <NotFoundScreen type="election" />;
    }

    // Show decryption error screen if decryption failed
    if (isDecryptionError) {
        return (
            <div className="result-container">
                <div className="language-switcher-container position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
                    <LanguageSwitcher />
                </div>

                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="result-card mt-5">
                                <div className="alert alert-danger" role="alert">
                                    <h4 className="alert-heading">{t('common:errors.decryptionTitle')}</h4>
                                    <p>{t('common:errors.decryptionMessage')}</p>
                                    <hr />
                                    <p className="mb-0">{t('common:errors.decryptionAction')}</p>
                                </div>
                                <div className="mt-3">
                                    <Link to="/" className="btn btn-outline-secondary">
                                        {t('common:navigation.backToHome')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="result-container">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="result-card mt-5">
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                                <div className="result-nav">
                                    <Link to="/" className="btn btn-outline-secondary me-2">
                                        {t('results:navigation.backToHome')}
                                    </Link>
                                    <Link to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}`, id)} className="btn btn-outline-primary">
                                        {t('results:navigation.backToElection')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Check if current language is RTL (Arabic)
    const isRTL = i18n.language === 'ar';

    return (
        <>
            <title>{title}</title>
            <meta name="description" content={description} />
            <div className={`result-container ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="language-switcher-container position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
                <LanguageSwitcher />
            </div>
            
            <div className="container-fluid">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-10 col-xl-8">
                        {/* Navigation */}
                        <div className="result-nav mb-4 pt-4">
                            <Link to="/" className="btn btn-outline-light btn-sm me-2">
                                {t('results:navigation.backToHome')}
                            </Link>
                            <Link to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}`, id)} className="btn btn-outline-light btn-sm">
                                {t('results:navigation.backToElection')}
                            </Link>
                        </div>

                        {/* Election Context Card */}
                        <div className="result-card mb-4">
                            <div className="result-header">
                                <h1 className="result-title">{t('results:header.title')}</h1>
                                <div className="election-context">
                                    <h2 className="election-name">{electionInfo.title}</h2>
                                    {electionInfo.description && (
                                        <p className="election-description"><LinkText text={electionInfo.description} /></p>
                                    )}
                                    <div className="result-summary">
                                        <span className="vote-count-badge">
                                            {t('results:votes.count', { count: electionResult?.totalVotes || 0 })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Results Display */}
                        {!electionResult || electionResult.totalVotes === 0 ? (
                            /* Empty State */
                            <div className="result-card">
                                <div className="empty-state">
                                    <div className="empty-state-icon">📊</div>
                                    <h3>{t('results:votes.noResults')}</h3>
                                    <p className="text-muted mb-4">
                                        {t('results:votes.resultsWillAppear')}
                                    </p>
                                    <div className="empty-state-actions">
                                        <Link
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}/create-vote`, id)}
                                            className="btn btn-primary btn-lg"
                                        >
                                            {t('results:actions.castVote')}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Results Display */
                            <div className="result-card mb-4">
                                <div className="results-header">
                                    <div className="d-flex justify-content-start align-items-start mb-2">
                                        <h3>{t('results:header.finalRanking')}</h3>
                                        <div className="flex-grow-1"></div>
                                        {(sortedRanks.length > 0 || sortedVetoedRanks.length > 0) && (
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={handleCopyRankingText}
                                                title={t('results:copyAsTextTitle')}
                                            >
                                                📋 {t('results:copyAsText')}
                                            </button>
                                        )}
                                    </div>

                                    {/* Copy Success Feedback */}
                                    {copySuccess && (
                                        <div className="alert alert-success mt-2 mb-3" role="alert">
                                            {copySuccess}
                                        </div>
                                    )}

                                    <p className="text-muted mb-0">{t('results:header.computedUsing')}</p>
                                </div>

                                {/* Veto Controls */}
                                <div className="veto-controls mb-4">
                                    <div className="d-flex align-items-center flex-wrap gap-4">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="considerVetoes"
                                                checked={areVetoesConsidered}
                                                onChange={(e) => setAreVetoesConsidered(e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="considerVetoes">
                                                <strong>{t('results:veto.considerVetoes')}</strong>
                                            </label>
                                        </div>
                                        {areVetoesConsidered && (
                                            <div className="d-flex align-items-center flex-wrap gap-2">
                                                <label htmlFor="vetoThreshold" className="form-label mb-0 flex-shrink-0">
                                                    <strong>{t('results:veto.vetoThreshold')}</strong>
                                                </label>
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        //type="number"
                                                        inputMode="decimal"
                                                        id="vetoThreshold"
                                                        className="form-control form-control-sm"
                                                        style={{ width: '70px', textAlign: 'center' }}
                                                        min="0"
                                                        max={electionResult?.totalVotes || 1}
                                                        value={vetoThreshold === 0 ? '' : vetoThreshold}
                                                        onChange={(e) => {
                                                            if (!e.target.value) {
                                                                setVetoThreshold(0);
                                                                return;
                                                            }
                                                             const value = Math.max(0, Math.min(electionResult?.totalVotes || 1, parseInt(e.target.value) || 0));
                                                             setVetoThreshold(value);
                                                        }}
                                                    />
                                                    <input
                                                        type="range"
                                                        id="vetoThresholdSlider"
                                                        className="form-range"
                                                        style={{ width: '120px' }}
                                                        min="1"
                                                        max={electionResult?.totalVotes || 1}
                                                        value={vetoThreshold}
                                                        onChange={(e) => setVetoThreshold(parseInt(e.target.value))}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {areVetoesConsidered && (
                                        <div className="mt-2">
                                            <small className="text-muted">
                                                {t('results:veto.vetoThresholdExplanation', { threshold: vetoThreshold })}
                                            </small>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Non-Vetoed Options Section */}
                                {areVetoesConsidered ? (
                                    <>
                                        <div className="rankings-section">
                                            <h4 className="section-title">{t('results:sections.nonVetoed', 'Non-Vetoed Options')}</h4>
                                            <div className="rankings-list">
                                                {sortedNonVetoedRanks.length === 0 ? (
                                                    <div className="text-muted text-center py-3">
                                                        {t('results:sections.noNonVetoedOptions', 'All options are vetoed')}
                                                    </div>
                                                ) : (
                                                    sortedNonVetoedRanks.map(rank => (
                                                        <div key={rank} className={`ranking-item ${rank === '1' ? 'winner' : ''}`}>
                                                            <div className="ranking-position">
                                                                <div className={`position-badge ${rank === '1' ? 'winner-badge' : ''}`}>
                                                                    {rank === '1' && <span className="winner-crown">👑</span>}
                                                                    #{rank}
                                                                </div>
                                                            </div>
                                                            <div className="ranking-content">
                                                                {nonVetoedOptions[rank].map((option, index) => 
                                                                    renderOptionWithVetoCount(option, nonVetoedOptions[rank].length > 1 && index === 0)
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>

                                        {/* Vetoed Options Section */}
                                        {sortedVetoedRanks.length > 0 && (
                                            <div className="rankings-section mt-4">
                                                <h4 className="section-title text-muted">{t('results:sections.vetoed', 'Vetoed Options')}</h4>
                                                <div className="rankings-list vetoed-section">
                                                    {sortedVetoedRanks.map(rank => (
                                                        <div key={rank} className="ranking-item vetoed">
                                                            <div className="ranking-position">
                                                                <div className="position-badge vetoed-badge">
                                                                    ✗
                                                                </div>
                                                            </div>
                                                            <div className="ranking-content">
                                                                {vetoedOptions[rank].map((option, index) => 
                                                                    renderOptionWithVetoCount(option, vetoedOptions[rank].length > 1 && index === 0)
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    /* Show all options when vetoes are not considered */
                                    <div className="rankings-list">
                                        {sortedRanks.map(rank => (
                                            <div key={rank} className={`ranking-item ${rank === '1' ? 'winner' : ''}`}>
                                                <div className="ranking-position">
                                                    <div className={`position-badge ${rank === '1' ? 'winner-badge' : ''}`}>
                                                        {rank === '1' && <span className="winner-crown">👑</span>}
                                                        #{rank}
                                                    </div>
                                                </div>
                                                <div className="ranking-content">
                                                    {optionsByRank[rank].map((option, index) => 
                                                        renderOptionWithVetoCount(option, optionsByRank[rank].length > 1 && index === 0, areVetoesConsidered)
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Methodology Card */}
                        {electionResult && electionResult.totalVotes > 0 && (
                            <div className="result-card mb-4">
                                <div className="methodology-header">
                                    <h3>{t('results:methodology.about')}</h3>
                                </div>
                                <div className="methodology-content">
                                    <p>
                                        {t('results:methodology.description')}
                                    </p>
                                    <div className="methodology-features">
                                        <div className="feature-item">
                                            <strong>✓ {t('results:methodology.features.condorcet')}:</strong> {t('results:methodology.condorcetDesc')}
                                        </div>
                                        <div className="feature-item">
                                            <strong>✓ {t('results:methodology.features.cloneIndependence')}:</strong> {t('results:methodology.cloneDesc')}
                                        </div>
                                        <div className="feature-item">
                                            <strong>✓ {t('results:methodology.features.monotonicity')}:</strong> {t('results:methodology.monotonicityDesc')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Actions Card */}
                        <div className="result-card mb-4">
                            <div className="result-actions-header">
                                <h3>{t('results:actions.title')}</h3>
                            </div>
                            
                            <div className="result-actions">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <Link
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}/votes`, id)}
                                            className="btn btn-outline-secondary btn-lg w-100 result-action-btn"
                                        >
                                            {t('election:actions.viewAllVotes')}
                                        </Link>
                                    </div>
                                    <div className="col-md-6">
                                        <Link
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}/create-vote`, id)}
                                            className="btn btn-primary btn-lg w-100 result-action-btn"
                                        >
                                            {t('election:actions.castYourVote')}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </>
    );
}

export default RankedElectionResult;